// src/pages/HomePage.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle,
  UserCheck,
  Play,
  ShieldCheck,
  Calendar,
} from "lucide-react";
import PlyrPlayer from "../components/PlyrPlayer";

/* ─── reusable check icon (matching DBC's CheckIcon style) ─── */
const Check: React.FC<{ className?: string }> = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-5 w-5 shrink-0 ${className}`}
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L9 11.586l6.293-6.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

/* ─── math/proof bullets (mirrors DBC proofBullets) ─── */
const proofBullets = [
  {
    title: "Small, consistent income adds up fast",
    body: "₦50,000 extra per week = ₦200,000/month. Over 12 months that's ₦2.4M — roughly 2–3× a typical mid-level salary increase. Built around your existing schedule.",
  },
  {
    title: "One paying client changes the math",
    body: "Charge ₦15,000 per client for a simple service. Get just 4 clients/month → ₦60,000/month. A single repeat client doubles that without extra effort.",
  },
  {
    title: "Digital products scale — one delivery, many buyers",
    body: "Create a workbook or mini-course once. Sell it to 100 people at ₦5,000 = ₦500,000 without trading more hours. That's leverage.",
  },
];

const HomePage: React.FC = () => {
  const [spotsRemaining] = useState<number>(18);

  return (
    <div className="min-h-screen bg-white text-[#1B2951]">
      {/* ── HERO — centered, dark bg, matching DBC header ── */}
      <header className="bg-gradient-to-b from-[#0f1724] to-[#1b2951] text-white py-20 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block bg-teal-300 text-[#032022] px-3 py-1 rounded-full font-semibold text-sm mb-6">
            Digital Income Blueprint
          </span>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            Your Skills Are Worth More Than Your Salary.{" "}
            <br className="hidden md:block" />
            Here's the Proof.
          </h1>

          <p className="text-xl md:text-2xl text-white/90 mb-6 leading-relaxed">
            You were trained to chase promotions. That gave stability — not
            freedom. But the same skills that keep your job can create
            repeatable income streams that out-earn your paycheck. This page
            proves how — with clear math, real examples, and a step-by-step
            plan.
          </p>

          <p className="text-base md:text-xl text-white/80 mb-8 font-semibold">
            Read the proof. See the path. Decide with confidence.
          </p>

          <a
            href="#problem"
            className="inline-block bg-teal-400 text-[#042027] px-8 py-4 rounded-2xl font-bold text-xl hover:bg-teal-300 transition-all transform hover:scale-105 shadow-xl"
          >
            Show Me the Proof →
          </a>
        </div>
      </header>

      {/* ── VIDEO — replaces DBC founder image ── */}
      <div
        className="w-full bg-[#0f1724]"
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="rounded-xl overflow-hidden shadow-2xl border-4 border-white/10">
            <PlyrPlayer
              provider="youtube"
              embedId="TXkDWBxu6eM"
              options={{
                controls: [
                  "play-large",
                  "play",
                  "progress",
                  "current-time",
                  "duration",
                  "mute",
                  "volume",
                  "captions",
                  "settings",
                  "pip",
                  "airplay",
                  "fullscreen",
                ],
                settings: ["captions", "quality", "speed"],
                speed: {
                  selected: 1,
                  options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
                },
                quality: {
                  default: 720,
                  options: [
                    4320, 2880, 2160, 1440, 1080, 720, 576, 480, 360, 240,
                  ],
                },
                youtube: {
                  noCookie: true,
                  rel: 0,
                  showinfo: 0,
                  iv_load_policy: 3,
                  modestbranding: 1,
                },
                autoplay: false,
                hideControls: true,
                resetOnEnd: false,
                keyboard: { focused: true, global: false },
                tooltips: { controls: true, seek: true },
              }}
              className="w-full"
            />
          </div>
          <p className="text-center text-white/50 text-sm mt-3">
            Watch: How busy Nigerian professionals build automated income —
            without quitting their jobs
          </p>
        </div>
      </div>

      {/* ── P = PROBLEM ── */}
      <section id="problem" className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">The Problem</h2>
          <p className="text-xl text-slate-500 mb-8 leading-relaxed">
            You work hard, you build competence, and you get paid a salary —
            usually tied to a job that could disappear overnight. Meanwhile,
            people with similar skills monetize them online and create multiple
            income streams. The core difference isn't talent — it's strategy.
          </p>

          <div className="bg-white rounded-2xl p-6 shadow-md text-left">
            <ul className="space-y-4">
              {[
                {
                  title: "Single income risk:",
                  body: "one payroll, one company, one source — if it disappears, everything stops.",
                },
                {
                  title: "Time-for-money trap:",
                  body: "your income grows only if you trade more hours or climb the corporate ladder — both have a hard ceiling.",
                },
                {
                  title: "Market confusion:",
                  body: "you don't know which skill will actually sell, to whom, or how to package it without sounding desperate.",
                },
              ].map(({ title, body }) => (
                <li key={title} className="flex items-start gap-3">
                  <div className="text-red-500 text-2xl leading-none">▸</div>
                  <div className="text-slate-700">
                    <strong>{title}</strong> {body}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── A = AMPLIFY ── */}
      <section className="py-12 px-6 bg-red-50">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Why that problem costs you real money
          </h3>
          <p className="text-xl text-slate-500 mb-8 leading-relaxed">
            Not starting isn't neutral — it's expensive. Below is a
            conservative, realistic example showing how modest side income
            compounds into substantial wealth over time.
          </p>

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-[#1b2951]">₦50k</div>
                <div className="text-sm text-slate-500 mt-1">
                  extra per week
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#1b2951]">₦200k</div>
                <div className="text-sm text-slate-500 mt-1">per month</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-red-500">₦2.4M</div>
                <div className="text-sm text-slate-500 mt-1">per year</div>
              </div>
            </div>
            <p className="mt-6 text-slate-600 leading-relaxed">
              If a small, consistent side income of ₦50,000/week sounds doable —
              that becomes ₦2.4M in a year. Compare that to typical raises or
              bonuses: side income compounds faster and is entirely in your
              control. You don't have to ask anyone for permission.
            </p>
          </div>
        </div>
      </section>

      {/* ── S = STORY / SOLUTION ── */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-6">
            A simple story — and the solution it revealed
          </h3>

          <p className="text-xl text-slate-500 mb-8 text-center leading-relaxed">
            I was a busy Nigerian professional with a steady salary. I taught
            people, managed projects, built things — but my income never matched
            the value I delivered at work. Instead of waiting for a promotion, I
            systematically turned one regular skill into a repeatable product
            and one reliable outreach channel. Within months, my side income
            matched a significant portion of my salary — while working only 2–3
            hours a week on it.
          </p>

          <div className="bg-slate-50 border-2 border-[#1b2951]/10 rounded-2xl p-6">
            <h4 className="font-bold text-[#1b2951] mb-4 text-lg">
              Three things changed everything:
            </h4>
            <ul className="space-y-3 text-slate-600">
              {[
                {
                  label: "Stop guessing:",
                  body: "discover which of your skills will actually sell and to which Nigerian buyer.",
                },
                {
                  label: "Build authority:",
                  body: "grow an audience that trusts you (email + WhatsApp) so selling feels natural, not pushy.",
                },
                {
                  label: "Convert simply:",
                  body: "use small, automated funnels that sell while you sleep — no complicated tech required.",
                },
              ].map(({ label, body }) => (
                <li key={label} className="flex items-start gap-3">
                  <Check className="text-teal-500 mt-0.5" />
                  <span>
                    <strong>{label}</strong> {body}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── T = TRANSFORMATION / PROOF ── */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Real proof — not hype
          </h3>
          <p className="text-xl text-slate-500 text-center mb-10 leading-relaxed">
            These are conservative, real-world examples showing how skills turn
            into cash — numbers are rounded to illustrate how the math works.
          </p>

          {/* Math proof cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-14">
            {proofBullets.map((p, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 shadow-md text-left"
              >
                <h4 className="font-bold text-lg mb-2 text-[#1b2951]">
                  {p.title}
                </h4>
                <p className="text-slate-500 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>

          {/* Outcome cards */}
          <div className="max-w-4xl mx-auto text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold mb-2">
              What you'll walk away with
            </h3>
            <p className="text-slate-500">
              Real, bankable outputs — not vague lessons. These are what we
              build together over 4 weeks.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "A Validated, Sellable Offer",
                desc: "A concrete product or service positioned for Nigerian buyers — with a clear price and full value stack ready to launch.",
              },
              {
                title: "An Audience That Buys",
                desc: "A growing email + WhatsApp list and a short nurture funnel that moves subscribers from curious to paying.",
              },
              {
                title: "An Automated Sales Path",
                desc: "A tested landing page, email sequence, and checkout that brings repeatable revenue — without constant hustle.",
              },
            ].map((b, i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-md p-6 flex flex-col gap-3"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-teal-500 shrink-0" />
                  <h3 className="font-semibold text-[#1b2951]">{b.title}</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── O = OFFER ── */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#0f1724] to-[#1b2951] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            Introducing the Digital Income Blueprint (Full Program)
          </h3>

          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            When you combine discovery (what to sell), authority (how to build
            an audience), and conversion (how to sell predictably), you remove
            guesswork and create income that scales. The Digital Income
            Blueprint is the step-by-step, 3-month system that does exactly that
            — built for busy Nigerian professionals.
          </p>

          {/* Curriculum module list */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-6 text-left">
            <h4 className="text-xl font-bold mb-6 text-center">
              What You'll Learn — Week by Week
            </h4>
            <div className="space-y-4">
              {[
                {
                  number: "1",
                  title: "Week 1 — Discover",
                  subtitle:
                    "Find your sweet spot: Passion + Skill + Market Demand. Build your Customer Avatar and run an MVP validation checklist so you know your offer will sell before you build it.",
                },
                {
                  number: "2",
                  title: "Week 2 — Build",
                  subtitle:
                    "Create your lead magnet, grow your WhatsApp list, batch content for the week ahead, and deploy a nurture sequence that primes your audience to buy.",
                },
                {
                  number: "3",
                  title: "Week 3 — Convert",
                  subtitle:
                    "Build a sales page with the proven formula, write your email + WhatsApp sales sequence, and wire up payment and checkout so buyers can pay you instantly.",
                },
                {
                  number: "4",
                  title: "Week 4 — Execute",
                  subtitle:
                    "Deploy the full funnel, collect first sales or signups, run real tests, and review performance data so you enter coaching with a live system — not a draft.",
                },
                {
                  number: "5–12",
                  title: "Weeks 5–12 — Coaching & Scale",
                  subtitle:
                    "Weekly live coaching calls, personalised feedback on your funnel submissions, and systematic optimization to double what's already working.",
                },
              ].map((m) => (
                <div
                  key={m.number}
                  className="border-b border-white/20 pb-4 last:border-0 flex items-start gap-4"
                >
                  <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shrink-0 text-sm">
                    {m.number}
                  </div>
                  <div>
                    <h5 className="font-bold text-lg mb-1">{m.title}</h5>
                    <p className="text-white/80 text-sm leading-relaxed">
                      {m.subtitle}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Price card */}
          <div className="bg-white text-[#1b2951] rounded-2xl p-6 mb-8">
            <div className="text-sm text-slate-500 mb-1">Program Length</div>
            <div className="text-2xl font-bold mb-1">3 months total</div>
            <div className="text-sm text-slate-500 mb-5">
              4 weeks core execution + 8 weeks live coaching support
            </div>
            {/* <div className="text-sm text-slate-500">Investment</div>
            <div className="text-4xl font-bold text-red-500 mb-1">₦49,999</div>
            <div className="text-sm text-slate-500">
              or split into 2 payments of ₦24,999
            </div> */}
          </div>

          <p className="text-white/80 mb-8 leading-relaxed">
            The program guides you from idea to first sale, then to an automated
            system that scales without more of your time. You get templates,
            scripts, and live coaching — plus accountability so you don't have
            to figure this out alone.
          </p>

          {/* <Link
            to="/sl?paymentType=full"
            className="inline-block bg-white text-[#1b2951] px-8 py-4 rounded-2xl font-bold text-xl hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
          >
            Enrol Now — ₦49,999 →
          </Link> */}
        </div>
      </section>

      {/* ── R = RESPONSE / CTA ── */}
      <section id="enrol" className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to turn your skills into a reliable income stream?
          </h3>

          <p className="text-xl text-slate-500 mb-8 leading-relaxed">
            Reserve your spot for the next cohort. Seats are intentionally
            limited to preserve personalised feedback and coaching quality —
            only{" "}
            <strong className="text-red-500">{spotsRemaining} spots</strong>{" "}
            remaining.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-10 text-left">
            {/* What's included */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="font-bold mb-4 text-[#1b2951]">What's included</h4>
              <ul className="space-y-3 text-slate-600">
                {[
                  "4-week intensive course (video + worksheets)",
                  "8 weeks of live coaching calls",
                  "WhatsApp community and accountability check-ins",
                  "Templates for sales pages, email sequences & offers",
                  "Funnel review and personalised feedback",
                  "Lifetime access to course recordings",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check className="text-red-500 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Guarantee + mini FAQ */}
            <div className="bg-green-50 border-2 border-green-500 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="text-green-600 h-5 w-5" />
                <h4 className="font-bold text-green-800">
                  30-day money-back guarantee
                </h4>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                Follow the system and implement the agreed actions. If you don't
                see the promised clarity or first traction within 30 days, we'll
                refund your full investment — no awkward questions asked.
              </p>

              <div className="space-y-4">
                {[
                  {
                    icon: (
                      <Calendar className="text-teal-500 h-5 w-5 shrink-0" />
                    ),
                    q: "How much time do I need?",
                    a: "Just 2–3 hours per week during the 4-week core phase. Coaching weeks require even less.",
                  },
                  {
                    icon: (
                      <UserCheck className="text-teal-500 h-5 w-5 shrink-0" />
                    ),
                    q: "Who is this for?",
                    a: "Busy Nigerian professionals who want to monetize an existing skill while keeping their current job.",
                  },
                  {
                    icon: <Play className="text-teal-500 h-5 w-5 shrink-0" />,
                    q: "What will I actually build?",
                    a: "A validated offer, a lead magnet, a landing page + email funnel, and a working checkout.",
                  },
                ].map(({ icon, q, a }) => (
                  <div
                    key={q}
                    className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm"
                  >
                    {icon}
                    <div>
                      <div className="font-semibold text-sm text-[#1b2951]">
                        {q}
                      </div>
                      <div className="text-sm text-slate-600 mt-0.5">{a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTAs */}
          {/* <div className="flex flex-col md:flex-row justify-center gap-4">
            <Link
              to="/sl?paymentType=full"
              className="inline-block bg-red-500 text-white px-8 py-4 rounded-2xl font-bold text-xl hover:bg-red-600 transition-all shadow-xl"
            >
              Enrol Now — ₦49,999
            </Link>
            <Link
              to="/sl?paymentType=split"
              className="inline-block border-2 border-[#1b2951] text-[#1b2951] px-8 py-4 rounded-2xl font-bold text-xl hover:bg-slate-50 transition-all"
            >
              Split — 2 × ₦24,999
            </Link>
          </div> */}

          <p className="mt-5 text-sm text-slate-400">
            Want to see inside first?{" "}
            <Link
              to="/preview"
              className="text-teal-600 underline hover:text-teal-700"
            >
              Get a free preview →
            </Link>
          </p>
        </div>
      </section>

      
    </div>
  );
};

export default HomePage;
